import React from 'react';
import styled, { css } from '../../styled-components';
import { media, pixelToRem, breakpoints } from '../../utilities';
import { LineTitle } from '../LineTitle/LineTitle.component';
import { Constrain, CONSTRAIN_SIZING } from '../ui';
import { ImageWithShapeOutline } from '../ImageWithShapeOutline/ImageWithShapeOutline.component';
import debounce from 'lodash.debounce';

const PullToConstrain = (CONSTRAIN_SIZING.normal - CONSTRAIN_SIZING.narrow) / 2;

interface IPageHeaderProps extends wmw.IBaseStyledComponentProps {
    highlightColor: wmw.ColorOptions;
    title: string;
    imageUrl?: string;
    imageAlt?: string;
    imageOutlineUrl?: string;
    lineTitle?: string;
}

const Title = styled.div<{ paddingOffset: number }>`
    width: 100%;
    max-width: 700px;
    clear: both;
    margin-bottom: 4rem;

    ${({ paddingOffset }) => media.m`
        clear: none;
        padding-top: ${paddingOffset}px;
    `}

    img {
        width: auto;
        visibility: hidden;
        display: block;
    }
`;

const Heading = styled.h1`
    font-size: ${({ theme }) => theme.fontNew.primary.size.level4.size};
    line-height: ${({ theme }) => theme.fontNew.primary.size.level4.lineHeight};
    font-weight: 500;
    margin-bottom: 0;

    ${media.xl`
        font-size: ${({ theme }) => theme.fontNew.primary.size.level2.size};
        line-height: ${({ theme }) =>
            theme.fontNew.primary.size.level2.lineHeight};
    `}
`;

// had to create this to prevent duplicate class names on build
const Image = styled(ImageWithShapeOutline)`
        margin: 0 0 ${pixelToRem(15)} 0;
        position: relative;

        ${media.m`
            max-width: 385px;
            margin-left: 1.6rem;
        `}

        ${media.l`
            margin-right: -${pixelToRem(PullToConstrain)};
        `}

        ${media.xl`
            max-width: 100%;
        `}
`;

class UnstyledPageHeader extends React.Component<IPageHeaderProps> {
    public state = {
        titlePadding: 0
    };

    private imageRef = React.createRef<HTMLImageElement>();

    private resizeHandler = debounce(() => {
        this.setTitlePaddingOffset(this.imageRef.current.offsetHeight);
    }, 100);

    public componentDidMount() {
        if (this.imageRef.current) {
            // onload not getting fired when image is served from serviceworker it seems so lets try this first then if onload fires use that
            if (this.imageRef.current.offsetHeight) {
                this.setTitlePaddingOffset(this.imageRef.current.offsetHeight);
            }

            this.imageRef.current.onload = () => {
                this.setTitlePaddingOffset(this.imageRef.current.offsetHeight);
            };

            window.addEventListener('resize', this.resizeHandler);
        }
    }

    public componentWillUnmount() {
        window.removeEventListener('resize', this.resizeHandler);
    }

    public render() {
        const {
            title,
            imageUrl,
            imageAlt,
            imageOutlineUrl,
            lineTitle,
            className
        } = this.props;
        return (
            <header className={className}>
                <Constrain type="narrow">
                    <div className="container">
                        <Image
                            src={imageUrl as any}
                            alt={imageAlt}
                            shapeOutline={imageOutlineUrl}
                            imageRef={this.imageRef}
                        />

                        <Title paddingOffset={this.state.titlePadding}>
                            {/* Kept in the document flow for layout and hidden in styles */}
                            {<LineTitle>{lineTitle && lineTitle}</LineTitle>}
                            <Heading data-testid="articleTitle">
                                {title}
                            </Heading>
                        </Title>
                    </div>
                </Constrain>
            </header>
        );
    }

    private setTitlePaddingOffset = (padding: number) => {
        const imageMidPoint = padding / 2;
        // additionalOffsetForHighlightBar is a magic number, it just looks right theres no science to this
        let additionalOffsetForHighlightBar = 10;

        if (
            window.matchMedia &&
            window.matchMedia(`(max-width: ${breakpoints.xl}px )`).matches
        ) {
            // at smaller view ports double the pull back as the highlight bar is thinner
            additionalOffsetForHighlightBar = 23;
        }

        this.setState({
            titlePadding: imageMidPoint - additionalOffsetForHighlightBar
        });
    };
}

export const PageHeader = styled(UnstyledPageHeader)`
    position: relative;
    margin-top: ${pixelToRem(32)};

    .container:after {
        content: '';
        clear: both;
        display: table;
    }

    ${LineTitle} {
        color: ${({ theme }) => theme.colors.deepBlue.color};
        ${({ lineTitle }) =>
            !lineTitle &&
            css`
                visibility: hidden;
            `}

        &:before {
            background-color: ${({ theme }) => theme.colors.deepBlue.color};
        }
    }

    ${Image} {
        &:before {
            content: '';
            position: absolute;
            background-color: ${({ theme, highlightColor }) =>
                theme.colors[highlightColor].color};
            top: 50%;
            left: -100vw;
            right: -100vw;
            z-index: -1;
            height: ${({ imageUrl }) => (imageUrl ? '80px' : '130px')};
            transform: translateY(-50%);

            ${({ imageUrl }) => css`
                ${media.xl`
                        height: ${imageUrl ? '140px' : '175px'};
                `}
            `}
        }
    }
`;
